/**
 * Cart section — table of orders ready for checkout with per-row selection,
 * plus Print All / Send Reminder CTAs.
 *
 * Checkout is two steps: pick the orders here, then hand the selection to the
 * payment section which takes the payment. The invoice is created on the way
 * so the payment step shows what Stripe actually owes, and a tap taken in the
 * Dashboard app has an invoice to match.
 */
import {
  CircleDollarSign,
  EllipsisVertical,
  Loader2,
  Printer,
  ShoppingCart,
  Undo2,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import type { OrderWithOwnerStripeUrl, OrderWithRelations } from '@snootsvet/shared'
import {
  formatClinicPrice,
  formatLatestWeight,
  formatName,
  matchesPredicate,
  ORDER_PREDICATES,
} from '@snootsvet/shared'

/**
 * Generate and preview a single prescription label in a new tab. `weight` is the
 * pre-formatted latest recorded weight (or null when none is on file).
 */
async function printSingleLabel(order: OrderWithRelations, weight: string | null) {
  try {
    await previewPdfLabels([orderToLabelData(order, weight)])
  } catch {
    toast.error('Failed to generate label')
  }
}

import { Button } from '@/frontend/components/ui/button'
import { Checkbox } from '@/frontend/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/frontend/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/frontend/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/frontend/components/ui/tooltip'
import {
  AppointmentGroupCell,
  computeAppointmentRowSpans,
} from '@/frontend/features/orders/appointment-group'
import { CheckoutPaymentSection } from '@/frontend/features/orders/checkout-screen/checkout-payment-section'
import { useCreateMemberInvoice } from '@/frontend/features/orders/hooks/use-create-member-invoice'
import {
  buildOrderFingerprint,
  type CheckoutInvoice,
} from '@/frontend/features/orders/hooks/use-handle-checkout'
import { useUpdateOrder } from '@/frontend/features/orders/hooks/use-update-order'
import { OrderStatusBadge, PaymentBadge } from '@/frontend/features/orders/order-badges'
import { OrderItemCell } from '@/frontend/features/orders/order-item-cell'
import { isOrderFeeUnzeroable, isOrderFeeZeroable } from '@/frontend/features/orders/orders.utils'
import {
  orderToLabelData,
  previewPdfLabels,
  shouldPrintLabel,
} from '@/frontend/features/printing/label-printing.utils'
import { usePrintLabels } from '@/frontend/features/printing/labels/use-print-labels'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useListPatientVitals } from '@/frontend/hooks/api/patients/use-list-patient-vitals'

export interface CheckoutCartSectionProps {
  /** Orders in the cart — prepared + administered-unpaid. */
  cartOrders: OrderWithOwnerStripeUrl[]
  /** Patient ID — used for sending collection notifications. */
  patientId: string
  /** Whether a mutation is in progress elsewhere — disables checkout actions. */
  mutating: boolean
  onMutatingChange: (mutating: boolean) => void
}

export function CheckoutCartSection({
  cartOrders,
  patientId,
  mutating,
  onMutatingChange,
}: CheckoutCartSectionProps) {
  const { region } = useViewerRegion()
  const { mutate: updateOrder } = useUpdateOrder()
  const { data: vitals } = useListPatientVitals(patientId)
  // Latest recorded weight, pre-formatted for the region — shown on printed labels.
  const latestWeight = formatLatestWeight(vitals, region)

  const rowSpans = useMemo(() => computeAppointmentRowSpans(cartOrders), [cartOrders])

  // ---- Selection ----

  // Tracks what's unticked rather than ticked, so orders arriving via polling
  // are selected by default and orders leaving the cart need no cleanup.
  const [deselectedIds, setDeselectedIds] = useState<ReadonlySet<string>>(() => new Set())

  const selectedOrders = useMemo(
    () => cartOrders.filter((o) => !deselectedIds.has(o.id)),
    [cartOrders, deselectedIds]
  )
  const allSelected = selectedOrders.length === cartOrders.length
  const noneSelected = selectedOrders.length === 0

  // ---- Checkout step ----

  // The selection is frozen when Checkout is clicked so orders arriving via polling
  // don't join an invoice that has already been created.
  const [checkout, setCheckout] = useState<{
    orderIds: string[]
    invoice: CheckoutInvoice | null
  } | null>(null)
  const checkoutOrders = useMemo(
    () => (checkout ? cartOrders.filter((o) => checkout.orderIds.includes(o.id)) : []),
    [cartOrders, checkout]
  )
  const { mutateAsync: createInvoice, isPending: isCreatingInvoice } = useCreateMemberInvoice()

  // A tap settles via webhook, so the orders simply drop out of the cart on the next
  // poll; that's the success signal. Polling can also empty the step for other reasons
  // (settled elsewhere), which just returns to the cart quietly.
  const [awaitingPayment, setAwaitingPayment] = useState(false)
  const checkoutEmptied = checkout !== null && checkoutOrders.length === 0
  useEffect(() => {
    if (!checkoutEmptied) return
    setCheckout(null)
    if (awaitingPayment) toast.success('Payment received')
  }, [checkoutEmptied, awaitingPayment])

  const startCheckout = async () => {
    const orderIds = selectedOrders.map((o) => o.id)
    const chargeableIds = selectedOrders.filter((o) => o.totalPrice > 0).map((o) => o.id)
    if (chargeableIds.length === 0) {
      setCheckout({ orderIds, invoice: null })
      return
    }
    onMutatingChange(true)
    try {
      const result = await createInvoice({
        memberId: cartOrders[0].patient.owner.id,
        orderIds: chargeableIds,
      })
      // Account credit covered the whole invoice: the backend settled the orders already.
      if (result.amountRemaining === 0) {
        toast.success('Covered by account credit')
        return
      }
      setCheckout({
        orderIds,
        invoice: {
          id: result.invoiceId,
          amountRemaining: result.amountRemaining,
          customerEmail: result.customerEmail,
          fingerprint: buildOrderFingerprint(selectedOrders, chargeableIds),
        },
      })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't create invoice")
    } finally {
      onMutatingChange(false)
    }
  }

  const toggleOrder = (id: string, selected: boolean) => {
    setDeselectedIds((prev) => {
      const next = new Set(prev)
      if (selected) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = (selected: boolean) => {
    setDeselectedIds(selected ? new Set() : new Set(cartOrders.map((o) => o.id)))
  }

  // ---- Derived values ----

  /** Orders with labels ready to print — take-home dispensations only. */
  const printableOrders = useMemo(() => cartOrders.filter(shouldPrintLabel), [cartOrders])

  const { print, isPrinting } = usePrintLabels()
  if (cartOrders.length === 0) return null

  if (checkout && checkoutOrders.length > 0) {
    return (
      <section className="rounded-lg border border-border p-3 shadow-xs">
        <CheckoutPaymentSection
          orders={checkoutOrders}
          invoice={checkout.invoice}
          onBack={() => setCheckout(null)}
          mutating={mutating}
          onMutatingChange={onMutatingChange}
          onAwaitingPaymentChange={setAwaitingPayment}
        />
      </section>
    )
  }

  return (
    <section className="rounded-lg border border-border p-3 shadow-xs">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-headline-sm text-muted-foreground">
          <ShoppingCart className="h-4 w-4" />
          Cart
        </h3>
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="outline"
                  disabled={printableOrders.length === 0 || isPrinting || mutating}
                  onClick={() =>
                    print(
                      printableOrders.map((order) => orderToLabelData(order, latestWeight)),
                      `labels-${cartOrders[0].patient.name.toLowerCase().replace(/\s+/g, '-')}.pdf`
                    )
                  }
                >
                  <Printer className="h-4 w-4" />
                  Print All Labels
                </Button>
              }
            />
            <TooltipContent>Prints labels for all prepared and completed items</TooltipContent>
          </Tooltip>

          <Button disabled={noneSelected || mutating} onClick={startCheckout}>
            {isCreatingInvoice ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ShoppingCart className="h-4 w-4" />
            )}
            Checkout {selectedOrders.length} {selectedOrders.length === 1 ? 'item' : 'items'}
          </Button>
        </div>
      </div>
      <Table size="sm" variant="borderless" className="table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-8">
              <Checkbox
                aria-label="Select all orders"
                checked={allSelected}
                indeterminate={!allSelected && !noneSelected}
                onCheckedChange={(checked) => toggleAll(checked)}
              />
            </TableHead>
            <TableHead className="w-6" />
            <TableHead className="w-40">Item</TableHead>
            <TableHead className="w-14 text-center">Qty</TableHead>
            <TableHead className="w-28">Prescriber</TableHead>
            <TableHead>Instructions</TableHead>
            <TableHead className="w-36 text-center">Status</TableHead>
            <TableHead className="w-28 text-center">Payment</TableHead>
            <TableHead className="w-32 text-center">Batch</TableHead>
            <TableHead className="w-16 text-right">Discount</TableHead>
            <TableHead className="w-24 text-right">Price</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {cartOrders.map((order, idx) => (
            <TableRow
              key={order.id}
              className={deselectedIds.has(order.id) ? 'text-muted-foreground/60' : undefined}
            >
              <TableCell>
                <Checkbox
                  aria-label={`Select ${order.product.name}`}
                  checked={!deselectedIds.has(order.id)}
                  onCheckedChange={(checked) => toggleOrder(order.id, checked)}
                />
              </TableCell>
              {rowSpans[idx] > 0 && <AppointmentGroupCell order={order} rowSpan={rowSpans[idx]} />}
              <OrderItemCell order={order} />
              <TableCell className="text-center">{order.quantity}</TableCell>
              <TableCell
                className="truncate text-muted-foreground"
                title={order.treatment?.staff ? formatName(order.treatment.staff) : undefined}
              >
                {order.treatment?.staff ? formatName(order.treatment.staff) : '—'}
              </TableCell>
              <TableCell
                className="max-w-0 truncate text-muted-foreground"
                title={order.treatment?.instructions ?? undefined}
              >
                {order.treatment?.instructions ?? '—'}
              </TableCell>
              <TableCell className="text-center">
                <OrderStatusBadge status={order.status} />
              </TableCell>
              <TableCell className="text-center">
                <PaymentBadge order={order} />
              </TableCell>
              <TableCell className="text-center">
                {order.allocations.length > 0 ? (
                  <span className="text-sm text-muted-foreground">
                    {order.allocations
                      .filter((a) => !a.reversedAt && a.stock.batchNumber)
                      .map((a) => `${a.stock.batchNumber} × ${a.quantity}`)
                      .join(', ')}
                  </span>
                ) : null}
              </TableCell>
              <TableCell className="text-right">
                {order.discountBps > 0 ? `${order.discountBps / 100}%` : '-'}
              </TableCell>
              <TableCell className="text-right">
                {formatClinicPrice(order.totalPrice, order.clinicId)}
              </TableCell>
              <TableCell className="text-end">
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={<Button size="icon" variant="ghost" aria-label="Row actions" />}
                  >
                    <EllipsisVertical className="h-4 w-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" size="sm">
                    {matchesPredicate(order, ORDER_PREDICATES.undoPrepare) && (
                      <DropdownMenuItem
                        onClick={() => updateOrder({ id: order.id, action: 'undo_prepare' })}
                      >
                        <Undo2 className="h-4 w-4" />
                        Undo Preparation
                      </DropdownMenuItem>
                    )}
                    {isOrderFeeZeroable(order) && (
                      <DropdownMenuItem
                        onClick={() => updateOrder({ id: order.id, action: 'zero_fee' })}
                        disabled={mutating}
                      >
                        <CircleDollarSign className="h-4 w-4" />
                        Zero Fee
                      </DropdownMenuItem>
                    )}
                    {isOrderFeeUnzeroable(order) && (
                      <DropdownMenuItem
                        onClick={() => updateOrder({ id: order.id, action: 'unzero_fee' })}
                        disabled={mutating}
                      >
                        <CircleDollarSign className="h-4 w-4" />
                        Restore Fee
                      </DropdownMenuItem>
                    )}
                    {shouldPrintLabel(order) && (
                      <DropdownMenuItem onClick={() => printSingleLabel(order, latestWeight)}>
                        <Printer className="h-4 w-4" />
                        Print label
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  )
}
